/**
 * useCredits Hooks
 * TanStack Query hooks for credit balance, history, usage, and purchases
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { creditsService, creditPurchaseService } from '@/services/api/profile';

export const creditBalanceQueryKey = ['credits', 'balance'] as const;
export const creditHistoryQueryKey = ['credits', 'history'] as const;
export const usageQueryKey = ['usage'] as const;
export const creditPricingQueryKey = ['credits', 'pricing'] as const;
export const creditOrdersQueryKey = ['credits', 'orders'] as const;

/**
 * Hook for fetching credit balance
 */
export function useCreditBalance() {
  return useQuery({
    queryKey: creditBalanceQueryKey,
    queryFn: () => creditsService.getBalance(),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook for fetching paginated credit transaction history
 */
export function useCreditHistory(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...creditHistoryQueryKey, limit, offset],
    queryFn: () => creditsService.getHistory(limit, offset),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook for fetching usage summary
 */
export function useUsageSummary(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: [...usageQueryKey, startDate, endDate],
    queryFn: () => creditsService.getUsage(startDate, endDate),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook for fetching credit pricing from backend
 */
export function useCreditPricing() {
  return useQuery({
    queryKey: creditPricingQueryKey,
    queryFn: () => creditPurchaseService.getPricing(),
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Hook for placing a credit purchase order
 */
export function usePlaceOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quantity: number) => creditPurchaseService.placeOrder(quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditBalanceQueryKey });
      queryClient.invalidateQueries({ queryKey: creditOrdersQueryKey });
      queryClient.invalidateQueries({ queryKey: creditHistoryQueryKey });
    },
  });
}

/**
 * Hook for fetching paginated order list
 */
export function useOrders(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...creditOrdersQueryKey, limit, offset],
    queryFn: () => creditPurchaseService.getOrders(limit, offset),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook for fetching a single order detail
 */
export function useOrderDetail(id: string | null) {
  return useQuery({
    queryKey: ['credits', 'order', id],
    queryFn: () => creditPurchaseService.getOrder(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook for cancelling a credit purchase order
 */
export function useCancelOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      creditPurchaseService.cancelOrder(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditOrdersQueryKey });
      queryClient.invalidateQueries({ queryKey: creditBalanceQueryKey });
      queryClient.invalidateQueries({ queryKey: creditHistoryQueryKey });
    },
  });
}
